import type { ZoneTreeNode } from "@gettactic/api";
import { useZonesTreeQuery } from "@gettactic/hooks";
import type { ZoneTypeDefinition } from "@gettactic/map-builder/src/types";
import { X } from "lucide-react";
import React from "react";
import { useShallow } from "zustand/react/shallow";
import type { AutoZonePick } from "../store/createEditorStore";
import { useEditorSelector, useEditorStore } from "../store/EditorStoreContext";
import { useActiveTool } from "../store/selectors";
import { approveAutoZones, defaultPickName } from "../tools/autoZoneCreate";

interface ZoneOption {
  tag: ZoneTypeDefinition;
  depth: number;
}

const flattenZones = (nodes: ZoneTreeNode[], depth = 0): ZoneOption[] =>
  nodes.flatMap((zone) => [
    {
      tag: {
        id: zone.id,
        name: zone.name,
        color: zone.color,
        icon: zone.icon ?? undefined
      },
      depth
    },
    ...flattenZones(zone.children ?? [], depth + 1)
  ]);

/** Floating card over the canvas while rooms are being picked: each pick
 *  with its name and zone, and the button that creates them all. */
export const AutoZonePickerCard: React.FC = () => {
  const store = useEditorStore();
  const { kind } = useActiveTool();
  const { picks, existingZones } = useEditorSelector(
    useShallow((state) => ({
      picks: state.chrome.autoZonePicks,
      existingZones: state.order.filter(
        (id) => state.shapes[id]?.type === "zone"
      ).length
    }))
  );
  const { data: tree } = useZonesTreeQuery(kind === "auto-zone");
  const options = React.useMemo(() => flattenZones(tree ?? []), [tree]);
  if (kind !== "auto-zone") return null;

  const updatePick = (position: number, patch: Partial<AutoZonePick>) =>
    store.getState().setChrome({
      autoZonePicks: picks.map((pick, index) =>
        index === position ? { ...pick, ...patch } : pick
      )
    });
  const removePick = (position: number) =>
    store.getState().setChrome({
      autoZonePicks: picks.filter((_, index) => index !== position)
    });
  const cancel = () => {
    store.getState().setChrome({ autoZonePicks: [], zoneToolTag: null });
    store.getState().setTool("select");
  };
  const approve = () => approveAutoZones(store);
  const count = picks.length;

  return (
    <div
      data-auto-zone-picker
      className="absolute top-4 left-4 z-10 flex w-72 flex-col gap-2 rounded-lg border border-neutral-200 bg-background-primary p-3 text-xs shadow-lg"
    >
      <span className="font-semibold text-neutral-800">Pick rooms</span>
      <p className="text-[11px] text-neutral-500">
        Click a room to add it, click again to drop it. Shift-click merges a
        room into the previous pick. Enter creates them all.
      </p>
      {count > 0 ? (
        <ul className="flex max-h-56 flex-col gap-1.5 overflow-y-auto">
          {picks.map((pick, index) => (
            <li
              // biome-ignore lint/suspicious/noArrayIndexKey: picks are positional
              key={index}
              data-auto-zone-pick
              className="flex flex-col gap-1 rounded-md bg-neutral-50 px-2 py-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-neutral-500">
                  {index + 1} · {pick.rooms.length} room
                  {pick.rooms.length === 1 ? "" : "s"}
                </span>
                <button
                  type="button"
                  className="rounded p-0.5 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700"
                  title="Drop this pick"
                  onClick={() => removePick(index)}
                >
                  <X size={12} />
                </button>
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  data-auto-zone-name
                  className="h-7 min-w-0 flex-1 rounded-md border border-neutral-200 bg-white px-2 text-xs outline-none focus:border-blue-400"
                  placeholder={defaultPickName(picks, index, existingZones)}
                  value={pick.name ?? ""}
                  onChange={(event) =>
                    updatePick(index, { name: event.target.value || null })
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") approve();
                    if (event.key === "Escape") cancel();
                    event.stopPropagation();
                  }}
                />
                <select
                  data-auto-zone-category
                  className="h-7 w-28 rounded-md border border-neutral-200 bg-white px-1 text-xs outline-none focus:border-blue-400"
                  title="Zone"
                  value={pick.tag?.id ?? ""}
                  style={
                    pick.tag
                      ? { borderLeftColor: pick.tag.color, borderLeftWidth: 3 }
                      : undefined
                  }
                  onChange={(event) =>
                    updatePick(index, {
                      tag:
                        event.target.value === pick.tag?.id
                          ? pick.tag
                          : (options.find(
                              (option) => option.tag.id === event.target.value
                            )?.tag ?? null)
                    })
                  }
                >
                  <option value="">No zone</option>
                  {pick.tag &&
                  !options.some((option) => option.tag.id === pick.tag?.id) ? (
                    // The pick's zone is not in the list (still loading, or
                    // not the organisation's): keep it selectable as is.
                    <option value={pick.tag.id}>{pick.tag.name}</option>
                  ) : null}
                  {options.map((option) => (
                    <option key={option.tag.id} value={option.tag.id}>
                      {`${" ".repeat(option.depth * 2)}${option.tag.name}`}
                    </option>
                  ))}
                </select>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[11px] text-neutral-400">Nothing picked yet.</p>
      )}
      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          className="rounded-md px-2 py-1 text-neutral-600 hover:bg-neutral-100"
          onClick={cancel}
        >
          Cancel
        </button>
        <button
          type="button"
          data-auto-zone-approve
          className="rounded-md bg-blue-600 px-2.5 py-1 font-medium text-white hover:bg-blue-700 disabled:opacity-40"
          disabled={count === 0}
          onClick={approve}
        >
          Create {count} zone{count === 1 ? "" : "s"}
        </button>
      </div>
    </div>
  );
};
